import React from "react";
import { motion, useReducedMotion } from "framer-motion";

/** Growth stages of the nav journey: seed → sprout → sapling → tree → fruiting tree */
export type GrowthStage = 0 | 1 | 2 | 3 | 4;

interface GrowthGlyphProps {
  stage: GrowthStage;
  /** Delay before the glyph starts growing, in seconds */
  delay?: number;
  className?: string;
}

const GROUND = "M4 20.5h16";

// Each stage draws its strokes in order (soil first, canopy last), then pops its blobs.
const STAGES: Record<GrowthStage, { strokes: string[]; blobs: { cx: number; cy: number; r: number }[] }> = {
  0: {
    strokes: [GROUND, "M12 18c-2.1 0-3.2-1.4-3.2-3S10.4 11.6 12 11c1.6.6 3.2 2.4 3.2 4s-1.1 3-3.2 3z"],
    blobs: [],
  },
  1: {
    strokes: [
      GROUND,
      "M12 20.5v-7",
      "M12 15.5c0-2.6-2-4.4-4.8-4.4 0 2.6 2 4.4 4.8 4.4z",
      "M12 13.5c0-2.8 2.1-4.8 5-4.8 0 2.8-2.1 4.8-5 4.8z",
    ],
    blobs: [],
  },
  2: {
    strokes: [
      GROUND,
      "M12 20.5V6.5",
      "M12 17c0-2.3-1.8-3.9-4.3-3.9 0 2.3 1.8 3.9 4.3 3.9z",
      "M12 13.2c0-2.3 1.8-3.9 4.3-3.9 0 2.3-1.8 3.9-4.3 3.9z",
      "M12 9.4c-.9-1.9-.4-3.9 1.4-5.1.9 1.9.4 3.9-1.4 5.1z",
    ],
    blobs: [],
  },
  3: {
    strokes: [
      GROUND,
      "M12 20.5v-6.5",
      "M12 16.5l-2.4-2",
      "M12 15.5l2.6-2.2",
      "M8.5 15A4.5 4.5 0 0 1 7 8.3a5 5 0 0 1 10 0A4.5 4.5 0 0 1 15.5 15z",
    ],
    blobs: [],
  },
  4: {
    strokes: [
      GROUND,
      "M12 20.5v-6.5",
      "M12 16.5l-2.4-2",
      "M12 15.5l2.6-2.2",
      "M8.5 15A4.5 4.5 0 0 1 7 8.3a5 5 0 0 1 10 0A4.5 4.5 0 0 1 15.5 15z",
    ],
    blobs: [
      { cx: 9.6, cy: 9.4, r: 1.05 },
      { cx: 14.3, cy: 8.2, r: 1.05 },
      { cx: 12.4, cy: 11.9, r: 1.05 },
    ],
  },
};

const STROKE_DURATION = 0.32;

const GrowthGlyph: React.FC<GrowthGlyphProps> = ({ stage, delay = 0, className }) => {
  const reduceMotion = useReducedMotion();
  const { strokes, blobs } = STAGES[stage];
  const blobDelay = delay + strokes.length * STROKE_DURATION * 0.7;

  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {strokes.map((d, i) => (
        <motion.path
          key={d}
          d={d}
          initial={reduceMotion ? false : { pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{
            pathLength: { delay: delay + i * STROKE_DURATION * 0.7, duration: STROKE_DURATION, ease: "easeOut" },
            opacity: { delay: delay + i * STROKE_DURATION * 0.7, duration: 0.01 },
          }}
        />
      ))}
      {blobs.map(({ cx, cy, r }, i) => (
        <motion.circle
          key={`${cx}-${cy}`}
          cx={cx}
          cy={cy}
          r={r}
          fill="currentColor"
          stroke="none"
          style={{ transformOrigin: `${cx}px ${cy}px` }}
          initial={reduceMotion ? false : { scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: blobDelay + i * 0.08, type: "spring", stiffness: 520, damping: 16 }}
        />
      ))}
    </svg>
  );
};

export default GrowthGlyph;
